import { AbstractBrowserTabEvent } from './AbstractBrowserTabEvent.mjs'

/**
 * A class to represent a MetadataEvent for logging sessions.
 * This type of event is recorded when metadata is retrieved for a page.
 */
export class MetadataEvent extends AbstractBrowserTabEvent {
  static TYPE = 'metadata'
  static COLUMNS = [
    ...AbstractBrowserTabEvent.COLUMNS,
    'url',
    'key',
    'value',
    'confidence',
    'frameId',
    'extractionType',
  ]
  /**
   * Constructor for the Metadata class.  This instantiates the
   * event object.
   *
   * @param {any} input a plain Metadata JSON object
   * @param {number} [input.time] the Unix-based timestamp (e.g. `new Date().getTime()`) of this event.  If not provided, the current time will be used.
   * @param {number} [input.tabId] the associated chrome-based tab id
   * @param {number} [input.frameId] the frame id within the tab, defaults to 0 if not provided - indicating the main frame.
   * @param {boolean} [input.redacted] whether or not this event contains redacted data.
   * @param {string} [input.extractionType] the method of extraction for this metadata event
   * @param {boolean} [input.implicit] whether or not this event is implicit (i.e. informational only, to provide context to event streams)
   * @param {string} [input.url] the new url of the tab.  This may be null if it's not needed.
   * @param {string} [input.key]
   * @param {string} [input.value]
   * @param {number} [input.confidence]
   * @constructor
   **/

  constructor({
    eventType = MetadataEvent.TYPE, // This property is here only for backwards compatibility with the old PdfTitleExtractionEvent
    time,
    redacted,
    implicit,
    tabId,
    frameId = 0,
    url,
    key,
    value,
    confidence,
    extractionType = MetadataEvent.ExtractionType.LEGACY,
    title = null, // This property is here for backwards compatibility with the old PdfTitleExtractionEvent
  }) {
    super(MetadataEvent.TYPE, time, redacted, implicit, tabId)
    this.url = url
    this.key = key
    this.value = value
    this.confidence = confidence
    this.frameId = frameId
    this.extractionType = extractionType

    if (eventType === 'pdfTitleExtraction') {
      this.key = 'extractedPdfTitle'
      this.value = title
    }
  }

  toJson() {
    const result = super.toJson()
    if (this.url) {
      result.url = this.url
    }
    if (this.key) {
      result.key = this.key
    }
    if (this.value) {
      result.value = this.value
    }
    if (this.confidence) {
      result.confidence = this.confidence
    }
    if (this.extractionType) {
      result.extractionType = this.extractionType
    }
    result.frameId = this.frameId ?? 0
    return result
  }

  redactHelper(hashFunction, result, k) {
    if (this[k] && typeof this[k] === 'string') {
      const redactedValue = hashFunction(this[k])
      result[redactedValue] = this[k]
      this[k] = redactedValue
    }
  }

  redact(hashFunction) {
    const result = super.redact(hashFunction)
    if (this.redacted) return result

    this.redactHelper(hashFunction, result, 'url')
    this.redactHelper(hashFunction, result, 'value')

    this.redacted = true

    return result
  }

  reveal(secrets) {
    if (!this.redacted) return
    if (!secrets)
      throw new Error('Cannot reveal secrets without a secrets object')

    const isStringValue = this.value && typeof this.value === 'string'

    const urlRevealed = !this.url || this.url in secrets
    const valueRevealed = !isStringValue || this.value in secrets

    if (urlRevealed && valueRevealed) {
      this.url &&= secrets[this.url]
      if (isStringValue) {
        this.value = secrets[this.value]
      }
      this.redacted = false
    }
  }

  redactedValues() {
    if (!this.redacted) {
      return []
    }
    const values = []
    if (this.url) {
      values.push(this.url)
    }
    if (this.value && typeof this.value === 'string') {
      values.push(this.value)
    }

    return values
  }

  /**
   * Enum for types of metadata extraction.
   * Each extraction type represents a different method or source for retrieving metadata.
   */
  static ExtractionType = {
    /**
     * Metadata extracted directly from PDF documents.
     * Used when the source document is a PDF and metadata is parsed from its contents.
     */
    PDF: 'pdf',
    /**
     * Metadata extracted using the Metascraper library.
     * Used for general web pages where Metascraper can parse metadata from HTML.
     */
    METASCRAPER: 'metascraper',
    /**
     * Legacy extraction method.
     * Used for older extraction logic or backward compatibility.
     */
    LEGACY: 'legacy',
    /**
     * Metadata extracted from Gale databases.
     * Used when the source is a Gale digital resource.
     */
    GALE: 'gale',
    /**
     * Metadata extracted from Gale Primary Sources.
     * Used for Gale's specialized primary source collections.
     */
    GALE_PRIMARY_SOURCE: 'galePrimarySource',
    /**
     * Metadata extracted from EBSCO databases.
     * Used when the source is an EBSCO digital resource.
     */
    EBSCO: 'ebsco',
    /**
     * Metadata extracted from EBSCOhost platform.
     * Used for resources accessed via EBSCOhost.
     */
    EBSCO_HOST: 'ebscoHost',
    /**
     * Metadata extracted from JSTOR.
     * Used when the source is a JSTOR digital resource.
     */
    JSTOR: 'jstor',
    /**
     * Metadata extracted from Infobase databases.
     * Used when the source is an Infobase digital resource.
     */
    INFOBASE: 'infobase',
  }
}
